import React, { useEffect, useMemo, useRef, useState } from 'react'
import { convertBytes } from 'utils/files'
import Select from 'components/atoms/Select'
import { ReactComponent as DeleteIcon } from 'assets/images/delete.svg'
import { useSelector } from 'react-redux'
import { getFileTypes } from 'ducks/files/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { selectBpUnits } from 'ducks/units/selectors'
import styled, { css } from 'styled-components'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { isNilOrEmpty, isNotNilOrEmpty } from 'utils/ramda'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import FileTypesSearch from 'features/bpDetails/Components/files/FileTypesSearch'
import EnhancedFileTypeSelector from 'features/bpDetails/Components/files/EnhancedFileTypeSelector'
import { propOr } from 'ramda'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import Input from 'components/atoms/Input'
import AddUsersToFile from 'features/bpDetails/Components/files/AddUsersToFile'
import { Tooltip } from '@mui/material'
import AttachTagsToUploadingFile from 'features/bpDetails/Components/files/AttachTagsToUploadingFiles'
import CircularProgress from '@mui/material/CircularProgress'
import ModalSelect from 'features/bpDetails/Components/files/ModalSelect'
import ModalDateInput from 'features/bpDetails/Components/files/ModalDateInput'
import { ApartmentOutlined, EventRepeatOutlined } from '@mui/icons-material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Checkbox from 'components/atoms/Checkbox'
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { formatDateWithTime } from '../../../../utils/date'
const defaultValues = {
  fileCategory: '',
  fileType: '',
  expirationDate: '',
  units: [],
  userIds: [],
  tagIds: [],
  onwardSharingOverride: null
}

const informationExchangeCategoryName = 'FRS Information Exchange'

const FileModalTableRow = ({
  file,
  onRemove,
  onSelect,
  isSubmitted,
  onValidationStateChange,
  onChange,
  bulkChangeValues,
  isSelected,
  initialType,
  initialCategory,
  initialTemplateId,
  initialUnitIds,
  initialTagIds,
  initialUserIds,
  unit,
  isPrivate,
  isChecklistLinkedUpload,
  currentStep,
  certaintyLevel,
  uploadStatus,
  createdAt,
  thumbnailUrl,
  isAI,
  showOnwardSharing = false,
  batchOnwardSharing = true
}) => {
  const [values, setValues] = useState(defaultValues)
  // Get file extension from either path or name
  const fileExtension = (file.path || file.name)?.split('.')?.pop() || ''
  const [displayName, setDisplayName] = useState(
    file.name
      ?.replace(`.${fileExtension}`, '')
      ?.replace(`.${fileExtension?.toUpperCase() || ''}`, '')
  )
  const [showPreview, setShowPreview] = useState(false)
  const previewRef = useRef(null)
  const initialValuesAppliedRef = useRef(false)
  const [editNameMode, setEditNameMode] = useState(false)
  const [tempDisplayName, setTempDisplayName] = useState(displayName)
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const bpUnits = useSelector(selectBpUnits)
  const bp = useSelector(getSelectedBp)
  const isValidExtensionForErp = ['pdf'].includes(
    fileExtension?.toLowerCase() || ''
  )

  const isUnit = isNotNilOrEmpty(unit)
  const shouldShowFixedUnitContext = isUnit && isChecklistLinkedUpload
  const shouldShowTags = !isPrivate && (!isUnit || isChecklistLinkedUpload)
  const isCompleted = uploadStatus === 'completed'
  const handleValueChange = (name, value) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const templateTagSelectionsByFileType = useMemo(() => {
    const allTemplates = bp?.checklistTemplates || bp?.checklist_templates || []
    const templates = isNotNilOrEmpty(initialTemplateId)
      ? allTemplates.filter(
          template => String(template?.id) === String(initialTemplateId)
        )
      : allTemplates

    return templates.reduce((acc, template) => {
      const templateItems =
        template?.items ||
        template?.analyticsTemplateItems ||
        template?.analytics_template_items ||
        []

      templateItems
        .filter(item => (item.itemType || item.item_type || 'file') === 'file')
        .forEach(item => {
          const fileTypeId = item.fileTypeId || item.file_type_id
          const tagIds = (
            item.bpTagRequirements ||
            item.bp_tag_requirements ||
            item.tagRequirements ||
            item.tag_requirements ||
            []
          )
            .map(
              tag =>
                tag?.fileTagId ||
                tag?.file_tag_id ||
                tag?.file_tag?.id ||
                tag?.fileTag?.id
            )
            .filter(Boolean)
            .map(String)

          if (!fileTypeId || tagIds.length === 0) return

          const key = String(fileTypeId)
          acc[key] = Array.from(new Set([...(acc[key] || []), ...tagIds]))
        })

      const fallbackByFileType =
        template?.bp_item_tag_requirements_by_file_type ||
        template?.bpItemTagRequirementsByFileType ||
        {}

      Object.entries(fallbackByFileType).forEach(([fileTypeId, tagIds]) => {
        if (!fileTypeId || !Array.isArray(tagIds) || tagIds.length === 0) return

        const key = String(fileTypeId)
        acc[key] = Array.from(
          new Set([...(acc[key] || []), ...tagIds.map(String)])
        )
      })

      const directRequirements = template?.item_tag_requirements || []
      directRequirements.forEach(requirement => {
        const fileTypeId = requirement?.file_type_id || requirement?.fileTypeId
        const tagIds =
          requirement?.file_tag_ids || requirement?.fileTagIds || []
        if (!fileTypeId || !Array.isArray(tagIds) || tagIds.length === 0) return

        const key = String(fileTypeId)
        acc[key] = Array.from(
          new Set([...(acc[key] || []), ...tagIds.map(String)])
        )
      })

      return acc
    }, {})
  }, [bp, initialTemplateId])

  const getAutoTagIdsForFileType = fileTypeId =>
    templateTagSelectionsByFileType[String(fileTypeId || '')] || []

  useEffect(() => {
    if (initialValuesAppliedRef.current) return

    const canApplyType =
      isNotNilOrEmpty(fileTypes) && isNotNilOrEmpty(initialType)
    const canApplyCategory =
      isNotNilOrEmpty(fileCategories) && isNotNilOrEmpty(initialCategory)

    if (!canApplyType && !canApplyCategory) return

    setValues(prev => ({
      ...prev,
      ...(canApplyType
        ? {
            fileType: initialType,
            tagIds: isNotNilOrEmpty(initialTagIds)
              ? initialTagIds.map(String)
              : templateTagSelectionsByFileType[String(initialType)] || []
          }
        : {}),
      ...(canApplyCategory ? { fileCategory: initialCategory } : {}),
      ...(isNotNilOrEmpty(initialUnitIds)
        ? { units: initialUnitIds.map(String) }
        : {}),
      ...(isNotNilOrEmpty(initialUserIds)
        ? { userIds: initialUserIds.map(String) }
        : {}),
      ...(!canApplyType && isNotNilOrEmpty(initialTagIds)
        ? { tagIds: initialTagIds.map(String) }
        : {})
    }))

    initialValuesAppliedRef.current = true
  }, [
    initialType,
    initialCategory,
    initialUnitIds,
    initialTagIds,
    initialUserIds,
    fileCategories,
    fileTypes,
    templateTagSelectionsByFileType
  ])

  const handleCategoryChange = (_, value) => {
    setValues(prev => ({
      ...prev,
      fileType: '',
      fileCategory: value,
      tagIds: []
    }))
  }

  const handleTypeChange = (_, value) => {
    const autoTagIds = getAutoTagIdsForFileType(value)

    setValues(prev => ({
      ...prev,
      fileType: value,
      tagIds: autoTagIds
    }))
  }

  const getConfidenceText = confidence => {
    if (!confidence || confidence === 0) return null

    if (confidence >= 70) return 'High'
    if (confidence >= 55) return 'Medium'
    return null
  }

  useEffect(() => {
    if (isNotNilOrEmpty(bulkChangeValues) && isSelected) {
      const { fileCategory, fileType, units, expirationDate, userIds, tagIds } =
        bulkChangeValues

      isNotNilOrEmpty(fileCategory) && handleCategoryChange('', fileCategory)
      isNotNilOrEmpty(fileType) && handleTypeChange('', fileType)
      isNotNilOrEmpty(units) && handleValueChange('units', units)
      isNotNilOrEmpty(expirationDate) &&
        handleValueChange('expirationDate', expirationDate)
      isNotNilOrEmpty(userIds) && handleValueChange('userIds', userIds)
      isNotNilOrEmpty(tagIds) && handleValueChange('tagIds', tagIds)
    }
  }, [bulkChangeValues])

  const categoriesOptions = fileCategories
    .filter(cat => cat.name !== informationExchangeCategoryName)
    .map(type => ({
      label: type.name,
      value: type.id
    }))

  const isErpFile = useMemo(() => {
    if (isNotNilOrEmpty(values.fileCategory)) {
      const selectedCategory = fileCategories.find(
        category => category.id === values.fileCategory
      )
      return selectedCategory?.name === 'Emergency Response Pack (ERP)'
    } else {
      return false
    }
  }, [values.fileCategory])

  const unitsOptions = bpUnits.map(type => ({
    label: type.name,
    value: type.id
  }))

  useEffect(() => {
    const { fileType, fileCategory } = values
    const erpValid = isErpFile ? isValidExtensionForErp : true

    const isValid = isAI
      ? true // AI upload doesn't require manual validation
      : isNotNilOrEmpty(fileType) && isNotNilOrEmpty(fileCategory) && erpValid

    onValidationStateChange(isValid)
  }, [values, isErpFile, isAI])

  useEffect(() => {
    if (isPrivate) {
      onChange({ file, values: { displayName } })
    } else {
      onChange({ file, values: { ...values, displayName } })
    }
  }, [values])

  const handleTypeAndCategoryChange = (category, type) => {
    category !== values.fileCategory && handleCategoryChange('', category)
    type !== values.fileType && handleTypeChange('', type)
  }

  const toggleEditNameMode = () => {
    setEditNameMode(prev => !prev)
  }

  const handleSaveDisplayName = () => {
    setDisplayName(tempDisplayName)
    toggleEditNameMode()
  }

  return (
    <Wrapper>
      <TableRow
        key={file.id}
        isUnit={isUnit}
        isPrivate={isPrivate}
        isChecklistLinkedUpload={isChecklistLinkedUpload}
        currentStep={currentStep}
        isAI={isAI}
      >
        <MobileRowHeader>
          <RadioCell>
            <input type='checkbox' onChange={onSelect} checked={isSelected} />
          </RadioCell>
          <FileTableCell>
            <FileNameWrapper>
              <FileIconWrapper currentStep={currentStep}>
                <FileIcon />
              </FileIconWrapper>
              <div>
                <FileNameEditWrapper>
                  {editNameMode ? (
                    <input
                      name='displayName'
                      value={tempDisplayName}
                      onChange={e => setTempDisplayName(e.target.value)}
                    />
                  ) : (
                    <>
                      <FileName isPrivate={isPrivate} fullName={!isUnit}>
                        {displayName}.{fileExtension}
                      </FileName>
                      {thumbnailUrl && (
                        <PreviewContainer>
                          <Tooltip title='Preview file'>
                            <PreviewIconWrapper
                              onClick={() => setShowPreview(true)}
                            >
                              <PreviewIcon />
                            </PreviewIconWrapper>
                          </Tooltip>

                          {showPreview && (
                            <PreviewPopup
                              ref={previewRef}
                              onClick={() => setShowPreview(false)}
                            >
                              <PreviewContent
                                onClick={e => e.stopPropagation()}
                              >
                                <PreviewHeader>
                                  <PreviewTitle>
                                    {displayName}.{fileExtension}
                                  </PreviewTitle>
                                  <PreviewCloseButton
                                    onClick={() => setShowPreview(false)}
                                  />
                                </PreviewHeader>
                                <PreviewBody>
                                  <ThumbnailWrapper>
                                    {thumbnailUrl ? (
                                      <PreviewImage
                                        src={thumbnailUrl}
                                        alt={displayName}
                                      />
                                    ) : (
                                      <PlaceholderPreview>
                                        <InsertDriveFileOutlinedIcon />
                                        <div>
                                          {fileExtension?.toUpperCase() ||
                                            'FILE'}
                                        </div>
                                      </PlaceholderPreview>
                                    )}
                                  </ThumbnailWrapper>
                                  <PreviewInfo>
                                    <div>
                                      <InfoRow>
                                        <InfoLabel>File Name:</InfoLabel>
                                        <InfoValue>
                                          {displayName}.{fileExtension}
                                        </InfoValue>
                                      </InfoRow>
                                      <InfoRow>
                                        <InfoLabel>Size:</InfoLabel>
                                        <InfoValue>
                                          {convertBytes(file.size)}
                                        </InfoValue>
                                      </InfoRow>
                                    </div>
                                    <div>
                                      <InfoRow>
                                        <InfoLabel>Type:</InfoLabel>
                                        <InfoValue>
                                          {fileExtension?.toUpperCase() ||
                                            'FILE'}
                                        </InfoValue>
                                      </InfoRow>
                                      {createdAt && (
                                        <InfoRow>
                                          <InfoLabel>Upload Time:</InfoLabel>
                                          <InfoValue>
                                            {formatDateWithTime(createdAt)}
                                          </InfoValue>
                                        </InfoRow>
                                      )}
                                    </div>
                                  </PreviewInfo>
                                </PreviewBody>
                              </PreviewContent>
                            </PreviewPopup>
                          )}
                        </PreviewContainer>
                      )}
                    </>
                  )}
                  {editNameMode ? (
                    <StyledSaveIcon
                      onClick={
                        isNotNilOrEmpty(tempDisplayName)
                          ? handleSaveDisplayName
                          : () => {}
                      }
                      disabled={isNilOrEmpty(tempDisplayName)}
                    />
                  ) : (
                    <StyledEditIcon onClick={toggleEditNameMode} />
                  )}
                </FileNameEditWrapper>
                <FileSize>{convertBytes(file.size)}</FileSize>
              </div>
            </FileNameWrapper>
          </FileTableCell>
        </MobileRowHeader>
        {!isPrivate && (
          <>
            {!isAI && (
              <TableCell data-label='Type'>
                <EnhancedFileTypeSelector
                  value={values.fileType}
                  onSelect={handleTypeAndCategoryChange}
                  hasError={isNilOrEmpty(values.fileType) && isSubmitted}
                  placeholder='Select file type...'
                />
              </TableCell>
            )}
            {!isUnit && (
              <TableCell data-label='Units'>
                <ModalSelect
                  icon={<ApartmentIcon />}
                  name='units'
                  value={values.units}
                  onChange={val => handleValueChange('units', val)}
                  options={unitsOptions}
                  disabled={isNilOrEmpty(unitsOptions)}
                  selectAll
                  multiple
                />
              </TableCell>
            )}
            {shouldShowFixedUnitContext && (
              <TableCell data-label='Unit'>
                <FixedContextCell>
                  <ApartmentIcon />
                </FixedContextCell>
              </TableCell>
            )}
            {!isUnit && (
              <>
                <TableCell data-label='Share'>
                  <Tooltip title='Manage permissions'>
                    <AddUsersToFile
                      isUpload
                      initiallySelected={values.userIds}
                      savedCount={values.userIds?.length || 0}
                      onSave={value => {
                        handleValueChange('userIds', value)
                      }}
                    />
                  </Tooltip>
                </TableCell>
              </>
            )}
            {shouldShowTags && (
              <TableCell data-label='Tags'>
                <Tooltip title='Manage tags'>
                  <AttachTagsToUploadingFile
                    savedCount={values.tagIds?.length || 0}
                    initiallySelected={values.tagIds}
                    onSave={value => {
                      handleValueChange('tagIds', value)
                    }}
                  />
                </Tooltip>
              </TableCell>
            )}
            <DateTableCell data-label='Expiry'>
              <ModalDateInput
                icon={<ExpirationIcon />}
                name='expirationDate'
                value={values.expirationDate}
                onChange={date => handleValueChange('expirationDate', date)}
              />
            </DateTableCell>
            <TableCell data-label='Delete'>
              <DeleteIconWrapper onClick={onRemove}>
                <DeleteIcon />
              </DeleteIconWrapper>
            </TableCell>
          </>
        )}
      </TableRow>
      {showOnwardSharing && currentStep === 1 && !isPrivate && (
        <OnwardRow data-tour-id='upload.onward-sharing-row'>
          <Checkbox
            size='large'
            name='onwardSharingOverride'
            label='Onward sharing allowed'
            isChecked={values.onwardSharingOverride ?? batchOnwardSharing}
            onChange={(name, checked) => handleValueChange('onwardSharingOverride', checked)}
          />
          <Tooltip
            arrow
            enterTouchDelay={0}
            leaveTouchDelay={6000}
            title='When ticked, your organisation’s Super Admins can also approve requests to open this file. When unticked, only you decide.'
          >
            <OnwardInfoButton type='button' aria-label='What does onward sharing allowed mean?'>
              <InfoOutlinedIcon style={{ fontSize: 20 }} />
            </OnwardInfoButton>
          </Tooltip>
        </OnwardRow>
      )}
      {isErpFile && !isValidExtensionForErp && (
        <TypeError>Only PDF files are allowed in the ERP category.</TypeError>
      )}
    </Wrapper>
  )
}

export default FileModalTableRow

const OnwardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 44px;
  padding: 0 16px 0 56px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 12px;
  }
`

const OnwardInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: #475467;
  cursor: pointer;

  &:hover {
    color: #0f6f78;
  }
`

const FileName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.haiti};
  max-width: 230px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ fullName }) =>
    fullName &&
    css`
      max-width: 550px;
    `}

  ${({ isPrivate }) =>
    isPrivate &&
    css`
      max-width: unset;
      width: 100%;
    `}

  @media (max-width: 1366px) {
    max-width: 200px;

    ${({ fullName }) =>
      fullName &&
      css`
        max-width: 350px;
      `}
  }

  @media (max-width: 1280px) {
    max-width: 160px;

    ${({ fullName }) =>
      fullName &&
      css`
        max-width: 280px;
      `}
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const FileSize = styled.div`
  font-size: 12px;
`

const RadioCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`

const FileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
`

const FileIconWrapper = styled.div`
  margin-right: 10px;
  min-width: 32px;
  min-height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fae6e1;

  @media (max-width: 1280px) {
    min-width: 28px;
    min-height: 28px;
    margin-right: 8px;
  }
`

const FileIcon = styled(InsertDriveFileOutlinedIcon)`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 16px !important;
`

const TableRow = styled.div`
  display: grid;
  margin-top: -1px;
  min-height: 72px;
  position: relative;
  cursor: pointer;

  &:last-of-type {
    border-radius: 0 0 8px 8px;
  }

  /* Traditional upload - with file type column */
  ${({ isAI, isUnit, isPrivate }) =>
    !isAI &&
    !isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px 60px 90px 50px;
    `}

  /* AI upload - no file type column */
  ${({ isAI, isUnit, isPrivate }) =>
    isAI &&
    !isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 60px 60px 60px 90px 50px;
    `}

  /* Unit uploads with traditional */
  ${({ isAI, isUnit, isPrivate }) =>
    !isAI &&
    isUnit &&
    !isPrivate &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px;
    `}

  ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
    !isAI &&
    isUnit &&
    !isPrivate &&
    isChecklistLinkedUpload &&
    css`
      grid-template-columns: 40px 1fr 2fr 60px 60px 60px 50px;
    `}

  /* Unit uploads with AI */
  ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
    isAI &&
    isUnit &&
    !isPrivate &&
    isChecklistLinkedUpload &&
    css`
      grid-template-columns: 40px 1fr 60px 60px 60px 50px;
    `}

  ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
    isAI &&
    isUnit &&
    !isPrivate &&
    !isChecklistLinkedUpload &&
    css`
      grid-template-columns: 40px 1fr 60px 60px;
    `}

  /* Private uploads */
  ${({ isPrivate }) =>
    isPrivate &&
    css`
      grid-template-columns: 40px minmax(300px, 1fr) 60px;
    `}

  @media (max-width: 1366px) {
    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px 50px 80px 50px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 50px 50px 50px 80px 50px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 2fr 50px 50px 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      !isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 50px 50px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 50px 50px 50px 50px;
      `}
  }

  @media (max-width: 1280px) {
    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      isAI &&
      !isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 45px 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      !isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 2fr 45px 45px 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      !isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 45px 45px;
      `}

    ${({ isAI, isUnit, isPrivate, isChecklistLinkedUpload }) =>
      isAI &&
      isUnit &&
      !isPrivate &&
      isChecklistLinkedUpload &&
      css`
        grid-template-columns: 40px 1fr 45px 45px 45px 45px;
      `}
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    margin: 12px 0;
    border: 1px solid #eaecf0;
    border-radius: 10px;
    background: #fff;
  }
`

const TableCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 13px;
  padding: 0 5px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    padding: 4px 0;
    gap: 10px;
  }

  @media (max-width: 768px) {
    &[data-label]::before {
      content: attr(data-label);
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.grey?.[700] || '#5f6b7a'};
      text-transform: uppercase;
      letter-spacing: 0.3px;
      min-width: 70px;
    }
  }
`

const DateTableCell = styled(TableCell)`
  justify-content: flex-start;
  padding-left: 10px;

  @media (max-width: 768px) {
    justify-content: space-between;
    padding-left: 0;
  }
`

const FixedContextCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const FileTableCell = styled(TableCell)`
  justify-content: flex-start;
  padding-left: 10px;

  @media (max-width: 768px) {
    padding-left: 0;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    width: 100%;

    &::before {
      content: none;
    }
  }
`

const MobileRowHeader = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;

    ${RadioCell} {
      flex: 0 0 auto;
      margin-top: 2px;
    }

    ${FileTableCell} {
      flex: 1;
      width: auto;
      padding: 0;
    }
  }
`

const DeleteIconWrapper = styled.div`
  cursor: pointer;
  padding: 3px;

  svg {
    cursor: pointer !important;
  }
`

const TypeError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  padding-left: 45px;
  margin-top: -10px;
`

const Wrapper = styled.div`
  border: 1px solid #eaecf0;

  @media (max-width: 768px) {
    border: none;
  }
`

const FileNameEditWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const StyledEditIcon = styled(EditIcon)`
  font-size: 14px !important;
  cursor: pointer !important;
`

const StyledSaveIcon = styled(SaveIcon)`
  font-size: 14px !important;
  cursor: pointer !important;
  color: ${({ theme, disabled }) =>
    disabled ? theme.colors.error : theme.colors.success};
`

const ApartmentIcon = styled(ApartmentOutlined)`
  cursor: pointer;
`

const ExpirationIcon = styled(EventRepeatOutlined)`
  cursor: pointer;
`
const PreviewContainer = styled.div`
  position: relative;
`

const PreviewIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const PreviewIcon = styled(VisibilityIcon)`
  font-size: 16px !important;
`

const PreviewPopup = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const PreviewContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.3);
`

const PreviewHeader = styled.div`
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[400]};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PreviewTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PreviewCloseButton = styled(CloseIcon)`
  cursor: pointer;
  font-size: 24px !important;
  color: ${({ theme }) => theme.colors.grey[100]};

  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
`

const PreviewBody = styled.div`
  padding: 24px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const ThumbnailWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 4px;
  margin-bottom: 20px;
  height: 500px;
  overflow: hidden;
`

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`

const PlaceholderPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.grey[100]};

  svg {
    margin-bottom: 16px;
    font-size: 80px !important;
  }

  div {
    font-size: 16px;
  }
`

const PreviewInfo = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`

const InfoRow = styled.div`
  display: flex;
  margin-bottom: 12px;
  font-size: 14px;
`

const InfoLabel = styled.div`
  font-weight: 600;
  width: 100px;
  color: ${({ theme }) => theme.colors.haiti};
`

const InfoValue = styled.div`
  color: ${({ theme }) => theme.colors.grey[100]};
`
